/** US states and DC by ISO 3166-2 subdivision code (the airport data's `iso_region`). */
export const US_STATES: Readonly<Record<string, string>> = {
  "US-AL": "Alabama",
  "US-AK": "Alaska",
  "US-AZ": "Arizona",
  "US-AR": "Arkansas",
  "US-CA": "California",
  "US-CO": "Colorado",
  "US-CT": "Connecticut",
  "US-DE": "Delaware",
  "US-DC": "District of Columbia",
  "US-FL": "Florida",
  "US-GA": "Georgia",
  "US-HI": "Hawaii",
  "US-ID": "Idaho",
  "US-IL": "Illinois",
  "US-IN": "Indiana",
  "US-IA": "Iowa",
  "US-KS": "Kansas",
  "US-KY": "Kentucky",
  "US-LA": "Louisiana",
  "US-ME": "Maine",
  "US-MD": "Maryland",
  "US-MA": "Massachusetts",
  "US-MI": "Michigan",
  "US-MN": "Minnesota",
  "US-MS": "Mississippi",
  "US-MO": "Missouri",
  "US-MT": "Montana",
  "US-NE": "Nebraska",
  "US-NV": "Nevada",
  "US-NH": "New Hampshire",
  "US-NJ": "New Jersey",
  "US-NM": "New Mexico",
  "US-NY": "New York State",
  "US-NC": "North Carolina",
  "US-ND": "North Dakota",
  "US-OH": "Ohio",
  "US-OK": "Oklahoma",
  "US-OR": "Oregon",
  "US-PA": "Pennsylvania",
  "US-RI": "Rhode Island",
  "US-SC": "South Carolina",
  "US-SD": "South Dakota",
  "US-TN": "Tennessee",
  "US-TX": "Texas",
  "US-UT": "Utah",
  "US-VT": "Vermont",
  "US-VA": "Virginia",
  "US-WA": "Washington State",
  "US-WV": "West Virginia",
  "US-WI": "Wisconsin",
  "US-WY": "Wyoming",
  "US-PR": "Puerto Rico",
};

/** Canadian provinces and territories by ISO 3166-2 code. */
export const CA_PROVINCES: Readonly<Record<string, string>> = {
  "CA-AB": "Alberta",
  "CA-BC": "British Columbia",
  "CA-MB": "Manitoba",
  "CA-NB": "New Brunswick",
  "CA-NL": "Newfoundland and Labrador",
  "CA-NS": "Nova Scotia",
  "CA-NT": "Northwest Territories",
  "CA-NU": "Nunavut",
  "CA-ON": "Ontario",
  "CA-PE": "Prince Edward Island",
  "CA-QC": "Quebec",
  "CA-SK": "Saskatchewan",
  "CA-YT": "Yukon",
};

/** Names people type for places, mapped to the city the airport data uses. Keys are folded. */
export const PLACE_ALIASES: Readonly<Record<string, string>> = {
  nyc: "New York",
  "new york city": "New York",
  manhattan: "New York",
  sf: "San Francisco",
  sfo: "San Francisco",
  "san fran": "San Francisco",
  la: "Los Angeles",
  dc: "Washington",
  "washington dc": "Washington",
  "washington d c": "Washington",
  vegas: "Las Vegas",
  philly: "Philadelphia",
  nola: "New Orleans",
  atl: "Atlanta",
  chi: "Chicago",
  "fort worth": "Dallas",
  "twin cities": "Minneapolis",
  "mexico df": "Mexico City",
};

/**
 * Busy airports, busiest first, used to rank a state's or country's cities when the airport data
 * (which has no passenger counts) rates them all "large". Roughly by annual passengers.
 */
export const MAJOR_HUBS: readonly string[] = [
  "ATL",
  "DXB",
  "DFW",
  "LHR",
  "HND",
  "DEN",
  "IST",
  "LAX",
  "ORD",
  "DEL",
  "CDG",
  "JFK",
  "AMS",
  "CAN",
  "SIN",
  "ICN",
  "MAD",
  "BKK",
  "PVG",
  "PEK",
  "FRA",
  "LAS",
  "MCO",
  "CLT",
  "MIA",
  "SEA",
  "SFO",
  "EWR",
  "PHX",
  "IAH",
  "BOM",
  "MEX",
  "YYZ",
  "SYD",
  "BCN",
  "FCO",
  "MUC",
  "LGW",
  "DOH",
  "KUL",
  "BOS",
  "MSP",
  "DTW",
  "FLL",
  "LGA",
  "PHL",
  "BWI",
  "SLC",
  "DCA",
  "IAD",
  "SAN",
  "TPA",
  "BNA",
  "AUS",
  "MDW",
  "HNL",
  "DAL",
  "PDX",
  "STL",
  "HOU",
  "RDU",
  "SMF",
  "MSY",
  "SJC",
  "SAT",
  "OAK",
  "SNA",
  "MCI",
  "CLE",
  "IND",
  "PIT",
  "CMH",
  "CVG",
  "JAX",
  "RSW",
  "OGG",
  "BDL",
  "ABQ",
  "MKE",
  "OMA",
  "YVR",
  "YUL",
  "YYC",
  "GRU",
  "BOG",
  "LIM",
  "SCL",
  "EZE",
  "GIG",
  "CUN",
  "LIS",
  "OPO",
  "DUB",
  "MAN",
  "ZRH",
  "VIE",
  "CPH",
  "OSL",
  "ARN",
  "HEL",
  "BRU",
  "MXP",
  "ATH",
  "PRG",
  "WAW",
  "BUD",
  "AGP",
  "PMI",
  "NCE",
  "EDI",
  "STN",
  "LYS",
  "GVA",
  "BER",
  "HAM",
  "DUS",
  "NRT",
  "KIX",
  "HKG",
  "TPE",
  "MNL",
  "CGK",
  "SGN",
  "HAN",
  "BLR",
  "MAA",
  "HYD",
  "JED",
  "RUH",
  "AUH",
  "CAI",
  "JNB",
  "CPT",
  "NBO",
  "ADD",
  "MEL",
  "BNE",
  "PER",
  "AKL",
  "FAO",
];

/**
 * Major cities by their native-script names, which fold to nothing in the Latin search, so
 * "北京" or "東京" finds Beijing or Tokyo. Matched on the whole trimmed query.
 */
export const NATIVE_NAMES: Readonly<Record<string, string>> = {
  北京: "Beijing",
  上海: "Shanghai",
  广州: "Guangzhou",
  廣州: "Guangzhou",
  深圳: "Shenzhen",
  成都: "Chengdu",
  香港: "Hong Kong",
  澳门: "Macau",
  澳門: "Macau",
  台北: "Taipei",
  臺北: "Taipei",
  东京: "Tokyo",
  東京: "Tokyo",
  大阪: "Osaka",
  京都: "Kyoto",
  名古屋: "Nagoya",
  福岡: "Fukuoka",
  札幌: "Sapporo",
  서울: "Seoul",
  首尔: "Seoul",
  부산: "Busan",
  新加坡: "Singapore",
  曼谷: "Bangkok",
  กรุงเทพ: "Bangkok",
  กรุงเทพมหานคร: "Bangkok",
  "Hà Nội": "Hanoi",
  河内: "Hanoi",
  胡志明市: "Ho Chi Minh City",
  吉隆坡: "Kuala Lumpur",
  雅加达: "Jakarta",
  马尼拉: "Manila",
  Москва: "Moscow",
  "Санкт-Петербург": "Saint Petersburg",
  Київ: "Kyiv",
  القاهرة: "Cairo",
  دبي: "Dubai",
  الرياض: "Riyadh",
  الدوحة: "Doha",
  "תל אביב": "Tel Aviv",
  Αθήνα: "Athens",
  मुंबई: "Mumbai",
  दिल्ली: "Delhi",
  "नई दिल्ली": "New Delhi",
  बेंगलुरु: "Bangalore",
  悉尼: "Sydney",
  墨尔本: "Melbourne",
  伦敦: "London",
  巴黎: "Paris",
  纽约: "New York",
  紐約: "New York",
  洛杉矶: "Los Angeles",
  旧金山: "San Francisco",
  ロンドン: "London",
  パリ: "Paris",
  ニューヨーク: "New York",
};
